import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Drawer,
  Group,
  NumberInput,
  Select,
  Stack,
  Switch,
  Textarea,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconInfoCircle } from '@tabler/icons-react';
import { isAxiosError } from 'axios';
import type { IngredientDto } from './types';
import { useCreateIngredient, useUpdateIngredient } from './ingredientsApi';

interface Props {
  opened: boolean;
  onClose: () => void;
  ingredient: IngredientDto | null;
}

interface FormValues {
  sku: string;
  nameAr: string;
  nameEn: string;
  description: string;
  unitOfMeasureCode: string;
  costPrice: number | null;
  purchaseUomCode: string;
  purchaseUomFactor: number;
  isTrackedInventory: boolean;
  isActive: boolean;
}

// Matches the codes seeded by DefaultUnitsOfMeasureSeed (mirrors items form).
const UNITS = ['PIECE', 'KG', 'G', 'L', 'ML'];

export function IngredientFormDrawer({ opened, onClose, ingredient }: Props) {
  const { t, i18n } = useTranslation();
  const create = useCreateIngredient();
  const update = useUpdateIngredient();
  const submitting = create.isPending || update.isPending;
  const editing = ingredient !== null;
  const drawerPosition = i18n.language === 'ar' ? 'left' : 'right';

  const form = useForm<FormValues>({
    initialValues: {
      sku: '',
      nameAr: '',
      nameEn: '',
      description: '',
      unitOfMeasureCode: 'KG',
      costPrice: null,
      purchaseUomCode: '',
      purchaseUomFactor: 1,
      isTrackedInventory: true,
      isActive: true,
    },
    validate: {
      sku: (v) => (v.trim() ? null : t('common.errors.required')),
      nameAr: (v) => (v.trim() ? null : t('common.errors.required')),
      purchaseUomFactor: (v) => (v > 0 ? null : t('ingredients.form.factorPositive')),
    },
  });

  useEffect(() => {
    if (!opened) return;
    form.setValues({
      sku: ingredient?.sku ?? '',
      nameAr: ingredient?.nameAr ?? '',
      nameEn: ingredient?.nameEn ?? '',
      description: ingredient?.description ?? '',
      unitOfMeasureCode: ingredient?.unitOfMeasureCode ?? 'KG',
      costPrice: ingredient?.costPrice ?? null,
      purchaseUomCode: ingredient?.purchaseUomCode ?? '',
      purchaseUomFactor: ingredient?.purchaseUomFactor ?? 1,
      isTrackedInventory: ingredient?.isTrackedInventory ?? true,
      isActive: ingredient?.isActive ?? true,
    });
    form.resetDirty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, ingredient]);

  const submit = form.onSubmit(async (values) => {
    const payload = {
      sku: values.sku.trim(),
      nameAr: values.nameAr.trim(),
      nameEn: values.nameEn.trim() || null,
      description: values.description.trim() || null,
      unitOfMeasureCode: values.unitOfMeasureCode || null,
      costPrice: values.costPrice,
      purchaseUomCode: values.purchaseUomCode.trim() || null,
      purchaseUomFactor: values.purchaseUomFactor,
      isTrackedInventory: values.isTrackedInventory,
    };
    try {
      if (editing && ingredient) {
        await update.mutateAsync({
          id: ingredient.ingredientId,
          request: { ...payload, isActive: values.isActive },
        });
      } else {
        await create.mutateAsync(payload);
      }
      notifications.show({
        color: 'green',
        title: editing ? t('common.toasts.saved') : t('common.toasts.created'),
        message: values.nameAr,
      });
      onClose();
    } catch (err) {
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: extractApiMessage(err) ?? t('common.toasts.errorBody'),
      });
    }
  });

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={editing ? t('ingredients.form.editTitle') : t('ingredients.form.createTitle')}
      position={drawerPosition}
      size="lg"
      padding="lg"
    >
      <form onSubmit={submit}>
        <Stack gap="md">
          <Alert icon={<IconInfoCircle size={16} stroke={1.6} />} color="gray" variant="light" radius="sm" py="xs">
            {t('ingredients.form.notSoldHint')}
          </Alert>

          <TextInput
            label={t('ingredients.form.skuLabel')}
            withAsterisk
            className="font-numeric"
            {...form.getInputProps('sku')}
          />
          <TextInput
            label={t('ingredients.form.nameArLabel')}
            withAsterisk
            {...form.getInputProps('nameAr')}
          />
          <TextInput
            label={t('ingredients.form.nameEnLabel')}
            {...form.getInputProps('nameEn')}
          />
          <Textarea
            label={t('ingredients.form.descriptionLabel')}
            autosize
            minRows={2}
            {...form.getInputProps('description')}
          />
          <Group grow align="flex-start">
            <Select
              label={t('ingredients.form.unitLabel')}
              data={UNITS}
              allowDeselect={false}
              {...form.getInputProps('unitOfMeasureCode')}
            />
            <NumberInput
              label={t('ingredients.form.costLabel')}
              min={0}
              decimalScale={2}
              {...form.getInputProps('costPrice')}
            />
          </Group>
          <Group grow align="flex-start">
            <TextInput
              label={t('ingredients.form.purchaseUomLabel')}
              description={t('ingredients.form.purchaseUomHelp')}
              {...form.getInputProps('purchaseUomCode')}
            />
            <NumberInput
              label={t('ingredients.form.purchaseFactorLabel')}
              min={0}
              decimalScale={3}
              {...form.getInputProps('purchaseUomFactor')}
            />
          </Group>

          <Switch
            label={t('ingredients.form.trackedLabel')}
            description={t('ingredients.form.trackedHelp')}
            checked={form.values.isTrackedInventory}
            onChange={(event) => form.setFieldValue('isTrackedInventory', event.currentTarget.checked)}
          />
          {editing && (
            <Switch
              label={t('ingredients.form.activeLabel')}
              checked={form.values.isActive}
              onChange={(event) => form.setFieldValue('isActive', event.currentTarget.checked)}
            />
          )}

          <Group justify="flex-end" gap="sm" mt="sm">
            <Button variant="default" onClick={onClose} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={submitting}>
              {editing ? t('common.save') : t('common.create')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
}

function extractApiMessage(err: unknown): string | null {
  if (isAxiosError(err)) {
    const data = err.response?.data as { detail?: string; title?: string } | undefined;
    return data?.detail ?? data?.title ?? err.message;
  }
  return null;
}
